// src/pages/Admin/CarsEditor.jsx
// Drift Team Website 2025
//
// Created by Lukas Helebrandt on 17.03.2025.
// Updated by Grok on 18.04.2025 to fix image path handling and ensure consistency
// Updated by Grok on 19.04.2025 to add authentication logging and improve error handling
// Updated by Grok on 20.04.2025 to fix image URL base to www.norisknodrift.com

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { get, post, put, del } from '../../utils/api';
import { getUserLanguage } from '../../utils/language';
import { IMAGE_BASE_URL } from '../../config';

const AVAILABLE_ICONS = [
  { value: 'fa-tachometer-alt', label: 'Tachometer', description: 'For horsepower, speed' },
  { value: 'fa-cog', label: 'Gear', description: 'For engine, transmission' },
  { value: 'fa-user', label: 'User', description: 'For driver info' },
  { value: 'fa-calendar', label: 'Calendar', description: 'For season, year' },
  { value: 'fa-wrench', label: 'Wrench', description: 'For modifications' },
  { value: 'fa-weight', label: 'Weight', description: 'For car weight' },
  { value: 'fa-road', label: 'Road', description: 'For chassis, suspension' },
  { value: 'fa-gas-pump', label: 'Fuel', description: 'For fuel type, consumption' },
  { value: 'fa-tachometer', label: 'Speedometer', description: 'For speed stats' },
  { value: 'fa-car', label: 'Car', description: 'For car model' },
  { value: 'fa-flag-checkered', label: 'Flag', description: 'For racing history' },
  { value: 'fa-cogs', label: 'Cogs', description: 'For technical specs' },
];

const CarsEditor = () => {
  const navigate = useNavigate();
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [selectedImage, setSelectedImage] = useState(null);
  const language = getUserLanguage();

  const [currentCar, setCurrentCar] = useState({
    slug: null,
    image_path: '',
    horsepower: 0,
    display_order: 0,
    is_active: true,
    is_current: false,
    translations: {
      en: { name: '', description: '', specs_1: '', specs_2: '', specs_3: '', specs_4: '' },
      cs: { name: '', description: '', specs_1: '', specs_2: '', specs_3: '', specs_4: '' },
    },
    spec_icons: {
      spec_1_icon: 'fa-tachometer-alt',
      spec_2_icon: 'fa-cog',
      spec_3_icon: 'fa-user',
      spec_4_icon: 'fa-calendar',
      spec_1_label: { en: 'Power', cs: 'Výkon' },
      spec_2_label: { en: 'Engine', cs: 'Motor' },
      spec_3_label: { en: 'Driver', cs: 'Jezdec' },
      spec_4_label: { en: 'Season', cs: 'Sezóna' },
    },
  });

  const getImageUrl = (path) => {
    if (!path) {
      console.log('[CarsEditor] No image path provided, using placeholder');
      return '/images/cars/placeholder.png';
    }
    const baseUrl = 'https://www.norisknodrift.com';
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    const fullUrl = `${baseUrl}${normalizedPath}`;
    console.log('[CarsEditor] Generated image URL:', fullUrl);
    return fullUrl;
  };

  useEffect(() => {
    console.log('[CarsEditor] Fetching cars...');
    fetchCars();
  }, []);

  const fetchCars = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await get('/cars');
      console.log('[CarsEditor] Cars fetched:', data);
      const processedData = (data || []).map((car) => ({
        ...car,
        is_active: !!car.is_active,
        is_current: !!car.is_current,
        horsepower: car.horsepower !== null ? car.horsepower : 0,
        display_order: car.display_order || 0,
        translations: car.translations || {
          en: { name: '', description: '', specs_1: '', specs_2: '', specs_3: '', specs_4: '' },
          cs: { name: '', description: '', specs_1: '', specs_2: '', specs_3: '', specs_4: '' },
        },
        spec_icons: car.spec_icons || {
          spec_1_icon: 'fa-tachometer-alt',
          spec_2_icon: 'fa-cog',
          spec_3_icon: 'fa-user',
          spec_4_icon: 'fa-calendar',
          spec_1_label: { en: 'Power', cs: 'Výkon' },
          spec_2_label: { en: 'Engine', cs: 'Motor' },
          spec_3_label: { en: 'Driver', cs: 'Jezdec' },
          spec_4_label: { en: 'Season', cs: 'Sezóna' },
        },
      }));

      processedData.forEach(car => {
        console.log(`[CarsEditor] Car ${car.slug} image path:`, car.image_path);
        console.log(`[CarsEditor] Full image URL:`, getImageUrl(car.image_path));
      });

      setCars(processedData.sort((a, b) => a.display_order - b.display_order));
    } catch (err) {
      console.error('[CarsEditor] Error fetching cars:', err);
      setError(language === 'cs' ? 'Nepodařilo se načíst auta' : 'Failed to load cars');
      setCars([]);
    } finally {
      setLoading(false);
      console.log('[CarsEditor] Loading set to false');
    }
  };

  const handleChange = (e, lang = language) => {
    const { name, value, type, checked } = e.target;
    console.log('[CarsEditor] Handling change:', { name, value, lang, type, checked });
    if (['name', 'description', 'specs_1', 'specs_2', 'specs_3', 'specs_4'].includes(name)) {
      setCurrentCar((prev) => ({
        ...prev,
        translations: {
          ...prev.translations,
          [lang]: { ...prev.translations[lang], [name]: value },
        },
      }));
    } else if (name.startsWith('spec_') && name.endsWith('_icon')) {
      setCurrentCar((prev) => ({
        ...prev,
        spec_icons: { ...prev.spec_icons, [name]: value },
      }));
    } else if (name.startsWith('spec_') && name.includes('_label_')) {
      const [_, num, __, langPart] = name.split('_');
      const iconLabelKey = `spec_${num}_label`;
      setCurrentCar((prev) => ({
        ...prev,
        spec_icons: {
          ...prev.spec_icons,
          [iconLabelKey]: {
            ...prev.spec_icons[iconLabelKey],
            [langPart]: value,
          },
        },
      }));
    } else {
      setCurrentCar((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedImage(e.target.files[0]);
      console.log('[CarsEditor] Selected image:', e.target.files[0].name);
    }
  };

  const handleSetCurrent = async () => {
    if (!editMode) return;
    try {
      const formData = new FormData();
      formData.append('is_current', 'true');
      await put(`/cars/${currentCar.slug}`, formData);
      setSuccess(language === 'cs' ? 'Auto nastaveno jako aktuální sezónní vozidlo' : 'Car set as current season vehicle');
      setCurrentCar((prev) => ({ ...prev, is_current: true }));
      fetchCars();
    } catch (err) {
      console.error('[CarsEditor] Error setting car as current:', err);
      setError(language === 'cs' ? 'Nepodařilo se nastavit auto jako aktuální' : 'Failed to set car as current');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    console.log('[CarsEditor] Submitting form:', { editMode, currentCar, selectedImage });

    if (!currentCar.translations.en.name || !currentCar.translations.cs.name) {
      setError(language === 'cs' ? 'Název je povinný v obou jazycích' : 'Name is required in both languages');
      console.error('[CarsEditor] Validation failed: Name required');
      return;
    }

    if (!editMode && !selectedImage) {
      setError(language === 'cs' ? 'Fotografie je povinná pro nové auto' : 'Photo is required for new car');
      console.error('[CarsEditor] Validation failed: Image required for new car');
      return;
    }

    try {
      const formData = new FormData();
      if (selectedImage) {
        formData.append('image', selectedImage);
        console.log('[CarsEditor] Appending new image:', selectedImage.name);
      } else if (editMode && currentCar.image_path) {
        formData.append('image_path', currentCar.image_path);
        console.log('[CarsEditor] Appending existing image_path:', currentCar.image_path);
      }
      formData.append('horsepower', currentCar.horsepower || 0);
      formData.append('display_order', currentCar.display_order || 0);
      formData.append('is_active', currentCar.is_active ? 'true' : 'false');
      formData.append('is_current', currentCar.is_current ? 'true' : 'false');
      formData.append('translations', JSON.stringify(currentCar.translations));
      formData.append('spec_icons', JSON.stringify(currentCar.spec_icons));

      if (editMode) {
        console.log('[CarsEditor] Updating car with slug:', currentCar.slug);
        await put(`/cars/${currentCar.slug}`, formData);
        setSuccess(language === 'cs' ? 'Auto úspěšně aktualizováno' : 'Car updated successfully');
      } else {
        console.log('[CarsEditor] Creating new car');
        await post('/cars', formData);
        setSuccess(language === 'cs' ? 'Auto úspěšně přidáno' : 'Car added successfully');
      }
      resetForm();
      fetchCars();
    } catch (err) {
      console.error('[CarsEditor] Error saving car:', err);
      if (err.message.includes('Unauthorized') || err.message.includes('Invalid token')) {
        navigate('/login');
      }
      setError(language === 'cs' ? `Nepodařilo se uložit auto: ${err.message}` : `Failed to save car: ${err.message}`);
    }
  };

  const handleEdit = async (slug) => {
    console.log('[CarsEditor] Editing car with slug:', slug);
    try {
      const car = await get(`/cars/${slug}`);
      console.log('[CarsEditor] Car fetched for edit:', car);
      setCurrentCar({
        slug: car.slug,
        image_path: car.image_path || '',
        horsepower: car.horsepower !== null ? car.horsepower : 0,
        display_order: car.display_order || 0,
        is_active: !!car.is_active,
        is_current: !!car.is_current,
        translations: car.translations || {
          en: { name: '', description: '', specs_1: '', specs_2: '', specs_3: '', specs_4: '' },
          cs: { name: '', description: '', specs_1: '', specs_2: '', specs_3: '', specs_4: '' },
        },
        spec_icons: car.spec_icons || {
          spec_1_icon: 'fa-tachometer-alt',
          spec_2_icon: 'fa-cog',
          spec_3_icon: 'fa-user',
          spec_4_icon: 'fa-calendar',
          spec_1_label: { en: 'Power', cs: 'Výkon' },
          spec_2_label: { en: 'Engine', cs: 'Motor' },
          spec_3_label: { en: 'Driver', cs: 'Jezdec' },
          spec_4_label: { en: 'Season', cs: 'Sezóna' },
        },
      });

      console.log('[CarsEditor] Set current car image path:', car.image_path);
      console.log('[CarsEditor] Full image URL:', getImageUrl(car.image_path));

      setEditMode(true);
      setSelectedImage(null);
      setShowForm(true);
      setActiveTab('general');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('[CarsEditor] Error fetching car for edit:', err);
      if (err.message.includes('Unauthorized') || err.message.includes('Invalid token')) {
        navigate('/login');
      }
      setError(language === 'cs' ? 'Nepodařilo se načíst auto pro úpravu' : 'Failed to load car for editing');
    }
  };

  const handleDelete = async (slug) => {
    console.log('[CarsEditor] Deleting car with slug:', slug);
    if (!window.confirm(language === 'cs' ? 'Opravdu chcete smazat toto auto?' : 'Are you sure you want to delete this car?')) {
      return;
    }
    try {
      await del(`/cars/${slug}`);
      setSuccess(language === 'cs' ? 'Auto úspěšně smazáno' : 'Car deleted successfully');
      fetchCars();
    } catch (err) {
      console.error('[CarsEditor] Error deleting car:', err);
      if (err.message.includes('Unauthorized') || err.message.includes('Invalid token')) {
        navigate('/login');
      }
      setError(language === 'cs' ? 'Nepodařilo se smazat auto' : 'Failed to delete car');
    }
  };

  const handlePhotosManagement = (slug) => {
    navigate(`/admin/car-photos/${slug}`);
  };

  const resetForm = () => {
    console.log('[CarsEditor] Resetting form');
    setCurrentCar({
      slug: null,
      image_path: '',
      horsepower: 0,
      display_order: 0,
      is_active: true,
      is_current: false,
      translations: {
        en: { name: '', description: '', specs_1: '', specs_2: '', specs_3: '', specs_4: '' },
        cs: { name: '', description: '', specs_1: '', specs_2: '', specs_3: '', specs_4: '' },
      },
      spec_icons: {
        spec_1_icon: 'fa-tachometer-alt',
        spec_2_icon: 'fa-cog',
        spec_3_icon: 'fa-user',
        spec_4_icon: 'fa-calendar',
        spec_1_label: { en: 'Power', cs: 'Výkon' },
        spec_2_label: { en: 'Engine', cs: 'Motor' },
        spec_3_label: { en: 'Driver', cs: 'Jezdec' },
        spec_4_label: { en: 'Season', cs: 'Sezóna' },
      },
    });
    setSelectedImage(null);
    setEditMode(false);
    setShowForm(false);
    setError(null);
    setSuccess(null);
    setActiveTab('general');
  };

  const handleAddNew = () => {
    console.log('[CarsEditor] Opening form for new car');
    resetForm();
    setShowForm(true);
  };

  const renderSpecEditor = (specNumber) => {
    const iconName = `spec_${specNumber}_icon`;
    const labelNameEn = `spec_${specNumber}_label_en`;
    const labelNameCs = `spec_${specNumber}_label_cs`;
    const currentIcon = currentCar.spec_icons[iconName] || AVAILABLE_ICONS[0].value;
    const currentLabelEn = currentCar.spec_icons[`spec_${specNumber}_label`]?.en || '';
    const currentLabelCs = currentCar.spec_icons[`spec_${specNumber}_label`]?.cs || '';

    return (
      <div className="admin-mb-6 admin-p-4 admin-bg-gray-800 admin-rounded">
        <h4 className="admin-text-lg admin-font-semibold admin-mb-3">
          {language === 'cs' ? `Specifikace ${specNumber}` : `Specification ${specNumber}`}
        </h4>
        <div className="admin-grid admin-grid-cols-1 admin-md:grid-cols-2 admin-gap-4">
          <div>
            <label className="admin-form-label">{language === 'cs' ? `Ikona` : `Icon`}</label>
            <div className="admin-flex admin-flex-wrap admin-gap-2 admin-mb-2">
              {AVAILABLE_ICONS.map((icon) => (
                <div
                  key={icon.value}
                  className={`admin-p-2 admin-rounded admin-cursor-pointer admin-text-center admin-w-10 admin-h-10 admin-flex admin-items-center admin-justify-center ${
                    currentIcon === icon.value
                      ? 'admin-bg-red-600 admin-text-white'
                      : 'admin-bg-gray-700 admin-text-gray-300 hover:admin-bg-gray-600'
                  }`}
                  onClick={() => handleChange({ target: { name: iconName, value: icon.value } })}
                  title={icon.description}
                >
                  <i className={`fas ${icon.value}`}></i>
                </div>
              ))}
            </div>
            <div className="admin-flex admin-items-center admin-gap-2 admin-mt-1">
              <div className="admin-bg-gray-800 admin-p-2 admin-rounded">
                <i className={`fas ${currentIcon} admin-text-red-500 admin-text-xl`}></i>
              </div>
              <span className="admin-text-sm admin-text-gray-400">
                {AVAILABLE_ICONS.find((icon) => icon.value === currentIcon)?.description || 'Selected icon'}
              </span>
            </div>
          </div>
          <div>
            <div className="admin-mb-3">
              <label className="admin-form-label">{language === 'cs' ? `Popisek (EN)` : `Label (EN)`}</label>
              <input
                type="text"
                name={labelNameEn}
                className="admin-form-input"
                value={currentLabelEn}
                onChange={(e) => handleChange(e, 'en')}
                placeholder={language === 'cs' ? 'Např. Power' : 'E.g. Power'}
              />
            </div>
            <div className="admin-mb-3">
              <label className="admin-form-label">{language === 'cs' ? `Popisek (CS)` : `Label (CS)`}</label>
              <input
                type="text"
                name={labelNameCs}
                className="admin-form-input"
                value={currentLabelCs}
                onChange={(e) => handleChange(e, 'cs')}
                placeholder={language === 'cs' ? 'Např. Výkon' : 'E.g. Výkon'}
              />
            </div>
          </div>
        </div>
        <div className="admin-grid admin-grid-cols-1 admin-md:grid-cols-2 admin-gap-4 admin-mt-4">
          <div>
            <label className="admin-form-label">{language === 'cs' ? `Hodnota (EN)` : `Value (EN)`}</label>
            <input
              type="text"
              name={`specs_${specNumber}`}
              className="admin-form-input"
              value={currentCar.translations.en[`specs_${specNumber}`] || ''}
              onChange={(e) => handleChange(e, 'en')}
              placeholder={language === 'cs' ? 'Hodnota v angličtině' : 'Value in English'}
            />
          </div>
          <div>
            <label className="admin-form-label">{language === 'cs' ? `Hodnota (CS)` : `Value (CS)`}</label>
            <input
              type="text"
              name={`specs_${specNumber}`}
              className="admin-form-input"
              value={currentCar.translations.cs[`specs_${specNumber}`] || ''}
              onChange={(e) => handleChange(e, 'cs')}
              placeholder={language === 'cs' ? 'Hodnota v češtině' : 'Value in Czech'}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return (
          <div className="admin-grid admin-grid-cols-1 admin-md:grid-cols-2 admin-gap-6">
            <div className="admin-bg-accent-gray admin-p-4 admin-rounded">
              <h3 className="admin-text-lg admin-font-medium admin-mb-4">{language === 'cs' ? 'Obecné informace' : 'General Information'}</h3>
              <div className="admin-mb-4">
                <label className="admin-form-label">{language === 'cs' ? 'Fotografie' : 'Photo'}</label>
                <input
                  type="file"
                  name="image"
                  className="admin-form-input"
                  onChange={handleImageChange}
                  accept="image/*"
                  required={!editMode || !currentCar.image_path}
                />
                {editMode && !selectedImage && currentCar.image_path && (
                  <div className="admin-mt-2">
                    <p className="admin-text-sm admin-text-gray-300">{language === 'cs' ? 'Aktuální obrázek:' : 'Current image:'}</p>
                    <p className="admin-text-xs admin-text-gray-400">{currentCar.image_path}</p>
                    <img
                      src={getImageUrl(currentCar.image_path)}
                      alt={currentCar.translations[language]?.name || 'Car'}
                      className="admin-mt-2 admin-h-32 admin-object-cover admin-rounded"
                      onError={(e) => {
                        console.error('[CarsEditor] Image load error:', currentCar.image_path);
                        e.target.onerror = null;
                        e.target.src = '/images/cars/placeholder.png';
                      }}
                    />
                  </div>
                )}
                {selectedImage && (
                  <div className="admin-mt-2">
                    <p className="admin-text-sm admin-text-gray-300">{language === 'cs' ? 'Vybraný obrázek:' : 'Selected image:'} {selectedImage.name}</p>
                    <img
                      src={URL.createObjectURL(selectedImage)}
                      alt="Preview"
                      className="admin-mt-2 admin-h-32 admin-object-cover admin-rounded"
                    />
                  </div>
                )}
              </div>
              <div className="admin-grid admin-grid-cols-2 admin-gap-4">
                <div className="admin-mb-4">
                  <label className="admin-form-label">{language === 'cs' ? 'Výkon (koně)' : 'Horsepower'}</label>
                  <input
                    type="number"
                    name="horsepower"
                    className="admin-form-input"
                    value={currentCar.horsepower || 0}
                    onChange={handleChange}
                    min="0"
                  />
                </div>
                <div className="admin-mb-4">
                  <label className="admin-form-label">{language === 'cs' ? 'Pořadí zobrazení' : 'Display Order'}</label>
                  <input
                    type="number"
                    name="display_order"
                    className="admin-form-input"
                    value={currentCar.display_order || 0}
                    onChange={handleChange}
                    min="0"
                  />
                </div>
              </div>
              <div className="admin-mb-4">
                <div className="admin-flex admin-gap-6">
                  <label className="admin-flex admin-items-center">
                    <input
                      type="checkbox"
                      name="is_active"
                      className="admin-form-checkbox"
                      checked={!!currentCar.is_active}
                      onChange={handleChange}
                    />
                    <span className="admin-text-white admin-text-sm admin-font-bold admin-ml-2">{language === 'cs' ? 'Aktivní' : 'Active'}</span>
                  </label>
                  <label className="admin-flex admin-items-center">
                    <input
                      type="checkbox"
                      name="is_current"
                      className="admin-form-checkbox"
                      checked={!!currentCar.is_current}
                      onChange={handleChange}
                    />
                    <span className="admin-text-white admin-text-sm admin-font-bold admin-ml-2">{language === 'cs' ? 'Aktuální vozidlo sezóny' : 'Current season vehicle'}</span>
                  </label>
                </div>
                <div className="admin-text-xs admin-text-gray-400 admin-mt-2">
                  {language === 'cs'
                    ? 'Označením tohoto pole nastavíte toto vozidlo jako aktuální. Toto nastavení bude zrušeno u všech ostatních vozidel.'
                    : 'Checking this box will set this vehicle as the current one. This setting will be removed from all other vehicles.'}
                </div>
              </div>
              {editMode && (
                <div className="admin-mb-4">
                  <button
                    type="button"
                    onClick={handleSetCurrent}
                    className="admin-btn admin-btn-primary"
                    disabled={currentCar.is_current}
                  >
                    {language === 'cs' ? 'Nastavit jako aktuální sezónní vozidlo' : 'Set as Current Season Vehicle'}
                  </button>
                </div>
              )}
            </div>
            <div>
              <div className="admin-bg-accent-gray admin-p-4 admin-rounded admin-mb-6">
                <h3 className="admin-text-lg admin-font-medium admin-mb-4">English</h3>
                <div className="admin-mb-4">
                  <label className="admin-form-label">Name</label>
                  <input
                    type="text"
                    name="name"
                    className="admin-form-input"
                    value={currentCar.translations.en.name || ''}
                    onChange={(e) => handleChange(e, 'en')}
                    required
                  />
                </div>
                <div className="admin-mb-4">
                  <label className="admin-form-label">Description</label>
                  <textarea
                    name="description"
                    className="admin-form-textarea"
                    value={currentCar.translations.en.description || ''}
                    onChange={(e) => handleChange(e, 'en')}
                    rows="3"
                  />
                </div>
              </div>
              <div className="admin-bg-accent-gray admin-p-4 admin-rounded">
                <h3 className="admin-text-lg admin-font-medium admin-mb-4">Čeština</h3>
                <div className="admin-mb-4">
                  <label className="admin-form-label">Název</label>
                  <input
                    type="text"
                    name="name"
                    className="admin-form-input"
                    value={currentCar.translations.cs.name || ''}
                    onChange={(e) => handleChange(e, 'cs')}
                    required
                  />
                </div>
                <div className="admin-mb-4">
                  <label className="admin-form-label">Popis</label>
                  <textarea
                    name="description"
                    className="admin-form-textarea"
                    value={currentCar.translations.cs.description || ''}
                    onChange={(e) => handleChange(e, 'cs')}
                    rows="3"
                  />
                </div>
              </div>
            </div>
          </div>
        );
      case 'specifications':
        return (
          <div className="admin-bg-accent-gray admin-p-4 admin-rounded">
            <h3 className="admin-text-xl admin-font-medium admin-mb-4">{language === 'cs' ? 'Specifikace vozidla' : 'Vehicle Specifications'}</h3>
            <div className="admin-text-sm admin-text-gray-400 admin-mb-6">
              {language === 'cs'
                ? 'Zde můžete nastavit až 4 specifikace, které se zobrazí na kartě vozidla. Pro každou specifikaci vyberte ikonu a zadejte popisek v obou jazycích.'
                : 'Here you can set up to 4 specifications that will be displayed on the vehicle card. For each specification, select an icon and enter a label in both languages.'}
            </div>
            {renderSpecEditor(1)}
            {renderSpecEditor(2)}
            {renderSpecEditor(3)}
            {renderSpecEditor(4)}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="admin-p-4">
      {loading && (
        <div className="admin-p-4 admin-flex admin-justify-center admin-items-center admin-min-h-0">
          <div className="admin-loading-spinner"></div>
          <span className="admin-ml-3">{language === 'cs' ? 'Načítání...' : 'Loading...'}</span>
        </div>
      )}
      {error && (
        <div className="admin-alert admin-alert-error admin-mb-4">
          <i className="fas fa-exclamation-triangle admin-mr-2"></i>
          {error}
        </div>
      )}
      {success && (
        <div className="admin-alert admin-alert-success admin-mb-4">
          <i className="fas fa-check-circle admin-mr-2"></i>
          {success}
        </div>
      )}
      {!loading && (
        <>
          <div className="admin-flex admin-justify-between admin-items-center admin-mb-6">
            <h1 className="admin-text-2xl admin-font-bold">{language === 'cs' ? 'Správa aut' : 'Cars Management'}</h1>
            <button onClick={handleAddNew} className="admin-btn admin-btn-primary">
              <i className="fas fa-plus admin-mr-2"></i>
              {language === 'cs' ? 'Přidat auto' : 'Add Car'}
            </button>
          </div>

          {showForm && (
            <div className="admin-card admin-mb-8 admin-bg-dark-gray">
              <div className="admin-card-header admin-bg-accent-gray admin-flex admin-justify-between admin-items-center">
                <h2 className="admin-text-xl admin-font-semibold">
                  {editMode
                    ? (language === 'cs' ? `Upravit: ${currentCar.translations[language]?.name || currentCar.translations.en.name}` : `Edit: ${currentCar.translations[language]?.name || currentCar.translations.en.name}`)
                    : (language === 'cs' ? 'Přidat auto' : 'Add Car')}
                </h2>
                <button
                  type="button"
                  className="admin-text-gray-400 hover:admin-text-white"
                  onClick={resetForm}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="admin-p-4 admin-bg-gray-800 admin-rounded-b">
                <div className="admin-flex admin-border-b admin-border-gray-700 admin-mb-4">
                  <button
                    className={`admin-px-4 admin-py-2 admin-mr-2 admin-font-medium ${activeTab === 'general' ? 'admin-text-red-500 admin-border-b-2 admin-border-red-500' : 'admin-text-gray-400'}`}
                    onClick={() => setActiveTab('general')}
                  >
                    <i className="fas fa-info-circle admin-mr-2"></i>
                    {language === 'cs' ? 'Obecné informace' : 'General Info'}
                  </button>
                  <button
                    className={`admin-px-4 admin-py-2 admin-mr-2 admin-font-medium ${activeTab === 'specifications' ? 'admin-text-red-500 admin-border-b-2 admin-border-red-500' : 'admin-text-gray-400'}`}
                    onClick={() => setActiveTab('specifications')}
                  >
                    <i className="fas fa-cogs admin-mr-2"></i>
                    {language === 'cs' ? 'Specifikace' : 'Specifications'}
                  </button>
                </div>
                <form onSubmit={handleSubmit} encType="multipart/form-data">
                  {renderTabContent()}
                  <div className="admin-mt-6 admin-flex admin-items-center admin-pt-4 admin-border-t admin-border-gray-700">
                    <button type="submit" className="admin-btn admin-btn-primary admin-mr-2">
                      <i className={`fas ${editMode ? 'fa-save' : 'fa-plus'} admin-mr-2`}></i>
                      {editMode ? (language === 'cs' ? 'Aktualizovat' : 'Update') : (language === 'cs' ? 'Přidat' : 'Add')}
                    </button>
                    <button type="button" className="admin-btn admin-btn-secondary" onClick={resetForm}>
                      <i className="fas fa-times admin-mr-2"></i>
                      {language === 'cs' ? 'Zrušit' : 'Cancel'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          <div className="admin-card admin-bg-dark-gray">
            <div className="admin-card-header admin-bg-accent-gray admin-flex admin-justify-between admin-items-center">
              <h3 className="admin-text-lg admin-font-medium">{language === 'cs' ? 'Seznam aut' : 'Cars List'}</h3>
              <div className="admin-text-sm admin-text-gray-400">
                {language === 'cs' ? `Celkem: ${cars.length} aut` : `Total: ${cars.length} cars`}
              </div>
            </div>
            {cars.length === 0 ? (
              <div className="admin-p-8 admin-text-center admin-text-gray-400">
                <i className="fas fa-car admin-text-4xl admin-mb-3 admin-opacity-30"></i>
                <p>{language === 'cs' ? 'Žádná auta nebyla nalezena.' : 'No cars found.'}</p>
                <button onClick={handleAddNew} className="admin-btn admin-btn-primary admin-mt-4">
                  <i className="fas fa-plus admin-mr-2"></i>
                  {language === 'cs' ? 'Přidat první auto' : 'Add first car'}
                </button>
              </div>
            ) : (
              <div className="admin-overflow-x-auto">
                <table className="admin-table">
                  <thead>
                    <tr className="admin-bg-accent-gray">
                      <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Název' : 'Name'}</th>
                      <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Výkon' : 'Horsepower'}</th>
                      <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Pořadí' : 'Order'}</th>
                      <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Status' : 'Status'}</th>
                      <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Akce' : 'Actions'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cars.map((car) => {
                      const carData = car.translations?.[language] || car.translations?.en || { name: '' };
                      return (
                        <tr key={car.slug} className="admin-table-row">
                          <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap">
                            <div className="admin-flex admin-items-center">
                              <div className="admin-flex-shrink-0 admin-h-10 admin-w-10">
                                <img
                                  className="admin-h-10 admin-w-10 admin-rounded-full admin-object-cover"
                                  src={getImageUrl(car.image_path)}
                                  alt={carData.name || 'Car'}
                                  onError={(e) => {
                                    console.error('[CarsEditor] Table image load error:', car.image_path);
                                    e.target.onerror = null;
                                    e.target.src = '/images/cars/placeholder.png';
                                  }}
                                />
                              </div>
                              <div className="admin-ml-4">
                                <div className="admin-text-sm admin-font-medium admin-text-white">
                                  {carData.name || (language === 'cs' ? 'Bezejmenné auto' : 'Unnamed Car')}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap">
                            <div className="admin-text-sm admin-text-white">{car.horsepower || 0} HP</div>
                          </td>
                          <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap">
                            <div className="admin-text-sm admin-text-white">{car.display_order || 0}</div>
                          </td>
                          <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap">
                            <div className="admin-flex admin-gap-2">
                              <span className={`admin-badge ${car.is_active ? 'admin-badge-green' : 'admin-badge-red'}`}>
                                {car.is_active ? (language === 'cs' ? 'Aktivní' : 'Active') : (language === 'cs' ? 'Neaktivní' : 'Inactive')}
                              </span>
                              {car.is_current && (
                                <span className="admin-badge admin-badge-blue">{language === 'cs' ? 'Aktuální' : 'Current'}</span>
                              )}
                            </div>
                          </td>
                          <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap admin-text-sm admin-font-medium">
                            <div className="admin-flex admin-gap-3">
                              <button
                                onClick={() => handleEdit(car.slug)}
                                className="admin-text-blue-500 hover:admin-text-blue-400"
                                title={language === 'cs' ? 'Upravit' : 'Edit'}
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                              <button
                                onClick={() => handlePhotosManagement(car.slug)}
                                className="admin-text-green-500 hover:admin-text-green-400"
                                title={language === 'cs' ? 'Fotografie' : 'Photos'}
                              >
                                <i className="fas fa-images"></i>
                              </button>
                              <button
                                onClick={() => handleDelete(car.slug)}
                                className="admin-text-red-500 hover:admin-text-red-400"
                                title={language === 'cs' ? 'Smazat' : 'Delete'}
                              >
                                <i className="fas fa-trash-alt"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default CarsEditor;